import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";

import {
  createCode,
  getMe,
  patchCode,
  PLACEMENTS,
  qrMeKey,
  qrWriteMessage,
  type QrCode,
  type QrMe,
  type QrPatch,
  type QrPlacement,
} from "./qr-api";
import { destinationReady, isHttpsUrl, placementLabel, placementsOk, printedAddress, redirectPreview, samePlacements } from "./qr-format";

// Writes for the QR Codes tab (spec: os-portal specs/qr-codes-module.md).
// The Worker enforces every rule (approver, live-code protection, validation);
// these controls only hide what the person's `/v1/qr/me` flags say they cannot do.

export const DEFAULT_FALLBACK = "https://ketosource.co/";

export function useQrMe(): QrMe | null {
  return useQuery({ queryKey: qrMeKey, queryFn: getMe, staleTime: 5 * 60_000 }).data?.data ?? null;
}

export function useRefreshQr() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: ["qr_codes"] });
}

/** One PATCH, then a refresh of every QR read. */
export function useQrPatch(id: string, onDone?: () => void) {
  const refresh = useRefreshQr();
  return useMutation({
    mutationFn: (body: QrPatch) => patchCode(id, body),
    onSuccess: () => {
      void refresh();
      onDone?.();
    },
  });
}

/** Where the code is printed. A code can sit in several places; at least one is required. */
function PlacementChecks({ id, value, onChange }: { id: string; value: QrPlacement[]; onChange: (value: QrPlacement[]) => void }) {
  return (
    <fieldset className="flex flex-col gap-2" id={id}>
      <legend className="text-sm font-medium text-ks-ink">Placements (at least one)</legend>
      <div className="flex flex-wrap gap-x-5 gap-y-2">
        {PLACEMENTS.map((placement) => (
          <div key={placement} className="flex items-center gap-2">
            <Checkbox
              id={`${id}-${placement}`}
              checked={value.includes(placement)}
              onCheckedChange={(checked) => onChange(checked === true ? [...value, placement] : value.filter((item) => item !== placement))}
            />
            <Label htmlFor={`${id}-${placement}`}>{placementLabel(placement)}</Label>
          </div>
        ))}
      </div>
    </fieldset>
  );
}

export function TextField({
  id,
  label,
  value,
  onChange,
  placeholder,
  disabled,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={value} placeholder={placeholder} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}

const SHORT_ID = /^[a-z0-9-]{2,40}$/;

/** `+ New code`: created as a draft; nothing printed changes until an approver takes it live. */
export function NewCodeButton({ onCreated }: { onCreated: (id: string) => void }) {
  const me = useQrMe();
  const refresh = useRefreshQr();
  const [open, setOpen] = useState(false);
  const blank = { label: "", id: "", placements: [] as QrPlacement[], product: "", campaign: "", print_run: "", target_url: "" };
  const [form, setForm] = useState(blank);
  const set = (key: "label" | "id" | "product" | "campaign" | "print_run" | "target_url") => (value: string) => setForm((prev) => ({ ...prev, [key]: value }));
  const create = useMutation({
    mutationFn: () =>
      createCode({
        id: form.id,
        printed_path: `/q/${form.id}`,
        label: form.label.trim(),
        placements: form.placements,
        product: form.product.trim() || null,
        campaign: form.campaign.trim() || null,
        print_run: form.print_run.trim() || null,
        target_url: form.target_url.trim(),
        fallback_url: DEFAULT_FALLBACK,
      }),
    onSuccess: () => {
      void refresh();
      setOpen(false);
      onCreated(form.id);
      setForm(blank);
    },
  });

  if (!me?.can_edit) return null;
  const valid = form.label.trim() !== "" && SHORT_ID.test(form.id) && placementsOk(form.placements) && isHttpsUrl(form.target_url.trim());
  return (
    <>
      <Button onClick={() => setOpen(true)}>+ New code</Button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) create.reset();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New code</DialogTitle>
            <DialogDescription>
              It starts as a draft. Printed codes pointing at a draft go to the old site until an approver takes it live.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <TextField id="qr-new-label" label="Name" value={form.label} onChange={set("label")} placeholder="Transparency · C8 MCT Oil" />
              <p className="text-sm text-muted-foreground">
                Name it <span className="font-medium">Funnel · Product</span>: the funnel it sends people to, then the product in plain words.
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <TextField
                id="qr-new-id"
                label="Short id (letters, numbers and hyphens)"
                value={form.id}
                onChange={(value) => set("id")(value.toLowerCase())}
                placeholder="c8-pack"
              />
              <p className="text-sm text-muted-foreground">
                Printed address: ketosource.co/q/{form.id || "<short id>"}
                {form.id !== "" && !SHORT_ID.test(form.id) && " (2 to 40 lowercase letters, numbers or hyphens)"}
              </p>
            </div>
            <PlacementChecks id="qr-new-placement" value={form.placements} onChange={(value) => setForm((prev) => ({ ...prev, placements: value }))} />
            <TextField id="qr-new-product" label="Product (optional)" value={form.product} onChange={set("product")} />
            <TextField id="qr-new-campaign" label="Campaign (optional)" value={form.campaign} onChange={set("campaign")} />
            <TextField id="qr-new-print-run" label="Print run (optional)" value={form.print_run} onChange={set("print_run")} />
            <TextField id="qr-new-target" label="Destination (https address)" value={form.target_url} onChange={set("target_url")} placeholder="https://" />
            {create.isError && <p className="text-sm text-ks-ink" role="alert">{qrWriteMessage(create.error)}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button disabled={!valid || create.isPending} onClick={() => create.mutate()}>
              {create.isPending ? "Creating…" : "Create draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Approver only. A live code needs its id typed, because every printed copy follows the change. */
export function ChangeDestinationButton({ code }: { code: QrCode }) {
  const me = useQrMe();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [typed, setTyped] = useState("");
  const change = useQrPatch(code.id, () => {
    setOpen(false);
    setUrl("");
    setTyped("");
  });

  if (!me?.can_approve) return null;
  const next = url.trim();
  const live = code.status === "live";
  const preview = isHttpsUrl(next) ? redirectPreview(next, code.tag_destination, code.id) : null;
  const ready = destinationReady({ next, current: code.target_url, live, typed, id: code.id });
  return (
    <>
      <Button size="sm" variant="outline" className="text-foreground" onClick={() => setOpen(true)}>
        Change destination…
      </Button>
      <Dialog
        open={open}
        onOpenChange={(value) => {
          setOpen(value);
          if (!value) change.reset();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change where {code.label} goes</DialogTitle>
            <DialogDescription>
              Everyone who scans this printed code from now on lands here. It takes up to a minute.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <TextField id="qr-dest-url" label="New destination (https address)" value={url} onChange={setUrl} placeholder="https://" />
            <div className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">Where a scan of {printedAddress(code.printed_url)} lands</span>
              <span className="font-medium break-all text-ks-ink" data-testid="qr-destination-preview">
                {preview ?? "Type a full https address to see the final address."}
              </span>
              <span className="text-muted-foreground">
                Anything extra on the printed address (for example ?src=pack) is added to the end, unless the destination already has it.
                {code.tag_destination &&
                  ` This code is tagged: utm_source=qr and utm_campaign=${code.id} are added too, unless already there.`}
                {next.includes("{key}") && " {key} is replaced by the batch number."}
              </span>
            </div>
            {live && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="qr-dest-confirm">This code is live. Type its id ({code.id}) to confirm.</Label>
                <Input id="qr-dest-confirm" value={typed} onChange={(event) => setTyped(event.target.value)} placeholder={code.id} />
              </div>
            )}
            {change.isError && <p className="text-sm text-ks-ink" role="alert">{qrWriteMessage(change.error)}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button disabled={!ready || change.isPending} onClick={() => change.mutate({ target_url: next })}>
              {change.isPending ? "Saving…" : "Change destination"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Go live / Pause / Resume / Retire, for approvers. A retired code has no actions. */
export function StatusActions({ code }: { code: QrCode }) {
  const me = useQrMe();
  const [retiring, setRetiring] = useState(false);
  const [typed, setTyped] = useState("");
  const setStatus = useQrPatch(code.id, () => {
    setRetiring(false);
    setTyped("");
  });

  if (!me?.can_approve || code.status === "retired") return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {code.status === "draft" && (
        <Button size="sm" disabled={setStatus.isPending} onClick={() => setStatus.mutate({ status: "live" })}>
          Go live
        </Button>
      )}
      {code.status === "live" && (
        <Button size="sm" variant="outline" className="text-foreground" disabled={setStatus.isPending} onClick={() => setStatus.mutate({ status: "paused" })}>
          Pause
        </Button>
      )}
      {code.status === "paused" && (
        <Button size="sm" variant="outline" className="text-foreground" disabled={setStatus.isPending} onClick={() => setStatus.mutate({ status: "live" })}>
          Resume
        </Button>
      )}
      {code.status !== "draft" && (
        <Button size="sm" variant="outline" className="text-foreground" onClick={() => setRetiring(true)}>
          Retire…
        </Button>
      )}
      {setStatus.isError && !retiring && <p className="text-sm text-ks-ink" role="alert">{qrWriteMessage(setStatus.error)}</p>}
      <Dialog
        open={retiring}
        onOpenChange={(value) => {
          setRetiring(value);
          if (!value) setStatus.reset();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Retire {code.label}?</DialogTitle>
            <DialogDescription>
              The printed code keeps working and goes to the fallback address, {code.fallback_url}. Type the code id ({code.id}) to confirm.
            </DialogDescription>
          </DialogHeader>
          <Input value={typed} onChange={(event) => setTyped(event.target.value)} aria-label="Code id" placeholder={code.id} />
          {setStatus.isError && <p className="text-sm text-ks-ink" role="alert">{qrWriteMessage(setStatus.error)}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRetiring(false)}>Cancel</Button>
            <Button variant="destructive" disabled={typed !== code.id || setStatus.isPending} onClick={() => setStatus.mutate({ status: "retired" })}>
              {setStatus.isPending ? "Saving…" : "Retire"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Edit users edit the details inline. The printed address is never editable. */
export function EditDetails({ code, onClose }: { code: QrCode; onClose: () => void }) {
  const me = useQrMe();
  const [form, setForm] = useState({
    placements: code.placements,
    product: code.product ?? "",
    campaign: code.campaign ?? "",
    print_run: code.print_run ?? "",
    notes: code.notes ?? "",
    fallback_url: code.fallback_url,
  });
  const save = useQrPatch(code.id, onClose);
  // Changing where a LIVE code falls back needs an approver; the Worker enforces it, the field just says so.
  const fallbackLocked = code.status === "live" && !me?.can_approve;
  const patch: QrPatch = {};
  if (!samePlacements(form.placements, code.placements)) patch.placements = form.placements;
  if (form.product !== (code.product ?? "")) patch.product = form.product.trim() || null;
  if (form.campaign !== (code.campaign ?? "")) patch.campaign = form.campaign.trim() || null;
  if (form.print_run !== (code.print_run ?? "")) patch.print_run = form.print_run.trim() || null;
  if (form.notes !== (code.notes ?? "")) patch.notes = form.notes.trim() || null;
  if (form.fallback_url !== code.fallback_url) patch.fallback_url = form.fallback_url.trim();
  const changed = Object.keys(patch).length > 0;
  const valid = isHttpsUrl(form.fallback_url.trim()) && placementsOk(form.placements);
  const set = (key: "product" | "campaign" | "print_run" | "notes" | "fallback_url") => (value: string) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="flex max-w-xl flex-col gap-4">
      <PlacementChecks id="qr-edit-placement" value={form.placements} onChange={(value) => setForm((prev) => ({ ...prev, placements: value }))} />
      <TextField id="qr-edit-product" label="Product" value={form.product} onChange={set("product")} />
      <TextField id="qr-edit-campaign" label="Campaign" value={form.campaign} onChange={set("campaign")} />
      <TextField id="qr-edit-print-run" label="Print run" value={form.print_run} onChange={set("print_run")} />
      <div className="flex flex-col gap-2">
        <Label htmlFor="qr-edit-notes">Notes</Label>
        <Textarea id="qr-edit-notes" value={form.notes} onChange={(event) => set("notes")(event.target.value)} />
      </div>
      <div className="flex flex-col gap-1">
        <TextField id="qr-edit-fallback" label="Fallback address" value={form.fallback_url} onChange={set("fallback_url")} disabled={fallbackLocked} />
        {fallbackLocked && <p className="text-sm text-muted-foreground">Only an approver can change the fallback of a live code.</p>}
      </div>
      {save.isError && <p className="text-sm text-ks-ink" role="alert">{qrWriteMessage(save.error)}</p>}
      <div className="flex gap-2">
        <Button disabled={!changed || !valid || save.isPending} onClick={() => save.mutate(patch)}>
          {save.isPending ? "Saving…" : "Save details"}
        </Button>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
      </div>
    </div>
  );
}
